import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'default-key-123'; // Should be set in env

class SecureStorageService {
  static STORAGE_KEYS = {
    USER_ROLES: 'userRoles',
    USER_CREATED: 'userCreated',
    USER_INITIALIZED: 'userInitialized',
    USER_INIT_IN_PROGRESS: 'userInitInProgress',
    LAST_ACTIVITY: 'lastActivity',
    PRE_LOGIN_PATH: 'preLoginPath'
  };

  static encrypt(data) {
    return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
  }

  static decrypt(encryptedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
      const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }

  static set(key, value, expirationInMinutes = null) {
    const item = {
      value,
      timestamp: new Date().getTime(),
      expiresIn: expirationInMinutes ? expirationInMinutes * 60 * 1000 : null // null means no expiration
    };
    const encrypted = this.encrypt(item);
    localStorage.setItem(key, encrypted);
  }

  static get(key) {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;

    const decrypted = this.decrypt(encrypted);
    if (!decrypted) {
      this.remove(key);
      return null;
    }

    // Check expiration only if expiresIn is set
    if (decrypted.expiresIn !== null) {
      const now = new Date().getTime();
      if (now - decrypted.timestamp > decrypted.expiresIn) {
        this.remove(key);
        return null;
      }
    }

    return decrypted.value;
  }

  static remove(key) {
    localStorage.removeItem(key);
  }

  static clear() {
    // Only clear our managed keys, preserve MSAL state
    Object.values(this.STORAGE_KEYS).forEach(key => {
      this.remove(key);
    });
  }

  static setRoles(roles) {
    this.set(this.STORAGE_KEYS.USER_ROLES, roles, null); // No expiration - persist indefinitely
  }

  static getRoles() {
    return this.get(this.STORAGE_KEYS.USER_ROLES);
  }

  static getSelectedProperty() {
    return localStorage.getItem('selectedProperty');
  }

  // Session management - simplified for persistent sessions
  static updateSessionTimestamp() {
    this.set(this.STORAGE_KEYS.LAST_ACTIVITY, new Date().getTime(), null); // No expiration
  }

  static isSessionValid() {
    // Always return true - sessions don't expire, only logout removes them
    return this.get(this.STORAGE_KEYS.LAST_ACTIVITY) !== null;
  }

  // User initialization tracking methods
  static isUserInitialized() {
    return this.get(this.STORAGE_KEYS.USER_INITIALIZED) === true;
  }

  static isUserInitializationInProgress() {
    return this.get(this.STORAGE_KEYS.USER_INIT_IN_PROGRESS) === true;
  }

  static startUserInitialization() {
    this.set(this.STORAGE_KEYS.USER_INIT_IN_PROGRESS, true, 5); // 5 minute timeout for safety
  }

  static completeUserInitialization() {
    this.set(this.STORAGE_KEYS.USER_INITIALIZED, true, null); // No expiration
    this.remove(this.STORAGE_KEYS.USER_INIT_IN_PROGRESS);
  }

  static resetUserInitialization() {
    this.remove(this.STORAGE_KEYS.USER_INITIALIZED);
    this.remove(this.STORAGE_KEYS.USER_INIT_IN_PROGRESS);
  }

  static clearAuthenticationData() {
    // Clear all authentication-related data
    this.clear();
    
    // Clear all MSAL cache - comprehensive cleanup
    const msalKeys = Object.keys(localStorage).filter(key => key.startsWith('msal.'));
    msalKeys.forEach(key => localStorage.removeItem(key));
    
    // Also clear sessionStorage completely for good measure
    window.sessionStorage.clear();
    
    console.log('Authentication data cleared completely');
  }

  /**
   * Migration utility: Migrate old localStorage data to SecureStorageService
   * This should be called once on app initialization
   */
  static migrateFromLocalStorage() {
    try {
      // Migrate userRoles if exists in localStorage
      const oldRoles = localStorage.getItem('userRoles');
      if (oldRoles) {
        try {
          const parsedRoles = JSON.parse(oldRoles);
          if (Array.isArray(parsedRoles) && parsedRoles.length > 0) {
            console.log('Migrating userRoles from localStorage to SecureStorageService');
            this.setRoles(parsedRoles);
          }
        } catch (parseError) {
          console.warn('Failed to parse old userRoles from localStorage:', parseError);
        }
        // Remove old data
        localStorage.removeItem('userRoles');
      }

      // Migrate userCreated if exists
      const oldUserCreated = localStorage.getItem('userCreated');
      if (oldUserCreated === 'true') {
        console.log('Migrating userCreated flag from localStorage');
        this.set(this.STORAGE_KEYS.USER_CREATED, true, 120);
        localStorage.removeItem('userCreated');
      }

      console.log('localStorage migration completed');
    } catch (error) {
      console.error('Error during localStorage migration:', error);
    }
  }
}

export default SecureStorageService;